import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { TenantConfig } from '../tenant-config';

interface MarketData {
  usdToVnd: number;
  domesticGoldPrice: {
    buy: number;
    sell: number;
    changeBuy: number;
    changeSell: number;
  } | null;
  internationalGoldPrice: {
    pricePerTaelVND: number;
  } | null;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const PriceChangeIndicator = ({ value }: { value: number }) => {
  if (value > 0) {
    return (
      <Box display="flex" alignItems="center" color="success.main">
        <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
        <Typography variant="caption" color="inherit">
          +{formatCurrency(value)}
        </Typography>
      </Box>
    );
  } else if (value < 0) {
    return (
      <Box display="flex" alignItems="center" color="error.main">
        <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
        <Typography variant="caption" color="inherit">
          {formatCurrency(value)}
        </Typography>
      </Box>
    );
  }
  return null;
};

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 10,
  boxShadow: '0 1px 8px 0 rgba(0,0,0,0.05)',
  marginBottom: theme.spacing(1.5),
  overflow: 'hidden',
  background: theme.palette.background.paper,
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  fontSize: '0.875rem',
}));

const MarketDataCard: React.FC<{ tenantConfig: TenantConfig }> = ({ tenantConfig }) => {
  const theme = useTheme();
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const response = await fetch('https://aws-ai-worker.doannq01.workers.dev/global/ty-gia', {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': tenantConfig.customApiKey
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch market data');
        }
        const data = await response.json();
        setMarketData(data.data);
      } catch (err) {
        console.error('Error fetching market data:', err);
        setError('Không thể tải dữ liệu thị trường');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarketData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMarketData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <StyledCard>
        <CardContent sx={{ py: 1.5, px: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="center" height={80}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </StyledCard>
    );
  }

  if (error || !marketData) {
    return (
      <StyledCard>
        <CardContent sx={{ py: 1.5, px: 2 }}>
          <Typography variant="body2" color="error" align="center">
            {error || 'Không có dữ liệu thị trường'}
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr' },
          gap: 0.75,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          fontSize: '0.875rem',
        }}>
          {/* Gold Prices */}
          {marketData.domesticGoldPrice && (
            <>
              <Box sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.warning.light, 0.1),
                    height: '100%',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <LocalAtmIcon color="warning" fontSize="inherit" sx={{ mr: 0.5, fontSize: '1rem' }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Vàng trong nước
                    </Typography>
                  </Box>

                  <Box mb={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Mua:</Typography>
                      <Box display="flex" alignItems="center">
                        <Typography variant="subtitle2" fontWeight={600} mr={0.25} sx={{ fontSize: '0.75rem' }}>
                          {formatCurrency(marketData.domesticGoldPrice.buy)}
                        </Typography>
                        {marketData.domesticGoldPrice.changeBuy !== 0 && (
                          <Box sx={{ transform: 'scale(0.8)', transformOrigin: 'right center' }}>
                            <PriceChangeIndicator value={marketData.domesticGoldPrice.changeBuy} />
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Bán:</Typography>
                      <Box display="flex" alignItems="center">
                        <Typography variant="subtitle2" fontWeight={600} mr={0.25} sx={{ fontSize: '0.75rem' }}>
                          {formatCurrency(marketData.domesticGoldPrice.sell)}
                        </Typography>
                        {marketData.domesticGoldPrice.changeSell !== 0 && (
                          <Box sx={{ transform: 'scale(0.8)', transformOrigin: 'right center' }}>
                            <PriceChangeIndicator value={marketData.domesticGoldPrice.changeSell} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.info.light, 0.1),
                    height: '100%',
                    width: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <LocalAtmIcon color="info" fontSize="inherit" sx={{ mr: 0.5, fontSize: '1rem' }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Vàng QT
                      </Typography>
                    </Box>
                    {marketData.internationalGoldPrice && (
                      <Typography variant="h6" fontWeight={700} color="primary" sx={{ fontSize: '0.875rem', lineHeight: 1.3 }}>
                        {formatCurrency(marketData.internationalGoldPrice.pricePerTaelVND)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {/* Exchange Rate - Moved to bottom */}
          <Box sx={{ mt: 0.75, gridColumn: '1 / -1', width: '100%' }}>
            <Paper
              variant="outlined"
              sx={{
                p: '6px 8px',
                borderRadius: 1.25,
                bgcolor: alpha(theme.palette.success.light, 0.1),
                borderColor: alpha(theme.palette.success.light, 0.3),
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <AttachMoneyIcon color="success" fontSize="inherit" sx={{ mr: 0.5, fontSize: '1rem' }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Tỷ giá USD/VND
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={700} color="success.dark" sx={{ fontSize: '0.875rem' }}>
                  {formatCurrency(marketData.usdToVnd)}/USD
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default MarketDataCard;
